/**
 * Developer portal for the Dunea marketplace (plugins, themes, templates,
 * skills): profile onboarding, CLI tokens, listings (checks, versions, review
 * status, analytics, test installs, deliveries, secret, reviews), earnings,
 * referrals, the expert profile and the wishlist.
 * Contract: packages/shared/src/marketplace/contracts.ts.
 */
import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGetDeveloperMe, apiListDevListings } from '~/lib/api/marketplace';
import { $currentUser } from '~/lib/stores/auth';
import { LoadError, Pill, Section, SkeletonCard } from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { PageHeading, PageShell } from '~/components/marketplace/PageShell';
import { founderTermsSentence } from '~/components/marketplace/developer-model';
import { EarningsSection } from '~/components/marketplace/developer/EarningsSection';
import { ExpertSection } from '~/components/marketplace/developer/ExpertSection';
import { ListingsSection } from '~/components/marketplace/developer/ListingsSection';
import { NewListing } from '~/components/marketplace/developer/NewListing';
import { Onboarding } from '~/components/marketplace/developer/Onboarding';
import { ReferralCard } from '~/components/marketplace/developer/ReferralCard';
import { StartBuilding } from '~/components/marketplace/developer/StartBuilding';
import { TokensSection } from '~/components/marketplace/developer/TokensSection';
import { WishClaimSection } from '~/components/marketplace/developer/WishClaimSection';

export default function DevelopersPage() {
  const user = useStore($currentUser);
  const me = useResource(() => apiGetDeveloperMe(), 'dev-me');

  return (
    <PageShell
      crumb="Developers"
      aside={
        user?.role === 'admin' && (
          <Link to="/developers/review" className="ui-btn-secondary !px-3 !py-1.5 text-[12px]">
            Review queue
          </Link>
        )
      }
    >
      {me.error ? (
        <LoadError message="Could not load your developer account." onRetry={() => void me.reload()} />
      ) : me.loading && !me.data ? (
        <div className="space-y-3">
          <SkeletonCard lines={2} />
          <SkeletonCard lines={4} />
        </div>
      ) : !me.data ? (
        <>
          <PageHeading
            caption="Build for Dunea"
            title={
              <>
                Give the Dunea AI <i className="text-accent">new skills.</i>
              </>
            }
            lede={`Publish plugins, themes, templates and skills for Dunea apps. ${founderTermsSentence()}`}
          />
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <Onboarding onCreated={(dev) => me.setData(dev)} />
            <StartBuilding />
          </div>
        </>
      ) : (
        <Portal devHandle={me.data.handle} verified={me.data.verified}>
          <TokensSection dev={me.data} onChanged={() => void me.reload()} />
        </Portal>
      )}
    </PageShell>
  );
}

function Portal({
  devHandle,
  verified,
  children,
}: {
  devHandle: string;
  verified: boolean;
  children: React.ReactNode;
}) {
  const listings = useResource(() => apiListDevListings(), 'dev-listings');
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeading
        caption={`@${devHandle}`}
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            Your listings
            {verified && <Pill tone="info" label="Verified" />}
          </span>
        }
      />

      <div className="grid gap-8 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-8">
          <Section
            title="Listings"
            icon="i-ph:squares-four"
            aside={
              !creating && (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="ui-btn-accent !px-3.5 !py-1.5 text-[12px]"
                >
                  <div className="i-ph:plus text-[13px]" />
                  New listing
                </button>
              )
            }
          >
            {creating && (
              <NewListing
                onCancel={() => setCreating(false)}
                onCreated={(listing) =>
                  listings.setData((prev) => [listing, ...(prev ?? []).filter((l) => l.slug !== listing.slug)])
                }
              />
            )}
            {listings.error ? (
              <LoadError message="Could not load your listings." onRetry={() => void listings.reload()} />
            ) : !listings.data ? (
              <>
                <SkeletonCard />
                <SkeletonCard />
              </>
            ) : (
              <ListingsSection
                listings={listings.data}
                onChanged={(next) =>
                  listings.setData((prev) => (prev ?? []).map((l) => (l.slug === next.slug ? next : l)))
                }
              />
            )}
          </Section>

          <EarningsSection />
          {/* Anchor for expert emails: /developers#experts */}
          <div id="experts" className="scroll-mt-20">
            <ExpertSection />
          </div>
        </div>

        <aside className="min-w-0 space-y-8">
          <ReferralCard />
          <WishClaimSection listings={listings.data ?? []} />
          <StartBuilding />
          {children}
        </aside>
      </div>
    </>
  );
}
